import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { DOC_TYPES } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Slider } from '../components/ui/slider';
import { ScrollText, Landmark, Users, ArrowLeft, Send, User as UserIcon, MapPin, Briefcase, Compass } from 'lucide-react';
import { toast } from 'sonner';

const NewApplication = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [akhing, setAkhing] = useState('');
  const [purpose, setPurpose] = useState('');
  const [days, setDays] = useState([30]);
  const [submitting, setSubmitting] = useState(false);

  // NOC overrides
  const [natureOverride, setNatureOverride] = useState(user?.natureOfBusiness || 'Trader');
  const [placeOverride, setPlaceOverride] = useState(user?.placeOfWork || '');

  // Land form
  const [land, setLand] = useState({
    documentNumber: '',
    totalArea: '',
    boundaryNorth: '',
    boundaryEast: '',
    boundaryWest: '',
    boundarySouth: '',
  });
  const setL = (k, v) => setLand((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!type) { toast.error('Choose document type'); return; }
    if (!akhing) { toast.error('Enter A\u2019khing / Village name'); return; }

    let payload = { type, akhing };

    if (type === 'NOC_TRADE') {
      if (days[0] > 89 || days[0] < 1) { toast.error('Days must be between 1 and 89'); return; }
      if (!placeOverride) { toast.error('Place of Work / Trade is required'); return; }
      payload = {
        ...payload,
        days: days[0],
        subPurpose: natureOverride,
        formDetails: { natureOfBusiness: natureOverride, placeOfWork: placeOverride, days: days[0] },
      };
    } else if (type === 'LAND') {
      const required = [
        ['documentNumber', 'Land Document Number'],
        ['totalArea', 'Total Area'],
        ['boundaryNorth', 'Boundary North'],
        ['boundaryEast', 'Boundary East'],
        ['boundaryWest', 'Boundary West'],
        ['boundarySouth', 'Boundary South'],
      ];
      for (const [k, l] of required) {
        if (!land[k] || !String(land[k]).trim()) { toast.error(`Please fill: ${l}`); return; }
      }
      payload = {
        ...payload,
        purpose: purpose || `Land Document #${land.documentNumber}`,
        formDetails: { ...land, akhing },
      };
    } else {
      if (!purpose) { toast.error('Please specify purpose'); return; }
      payload = { ...payload, purpose };
    }

    setSubmitting(true);
    try {
      await api.post('/applications', payload);
      toast.success('Application submitted. Routed to Jakrikra.');
      navigate('/applicant');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button variant="ghost" onClick={() => navigate('/applicant')} className="mb-4 gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <Card className="border-border shadow-warm">
          <CardHeader>
            <CardTitle className="font-serif text-2xl">New Application</CardTitle>
            <CardDescription>Your registered details will be used. Confirm a few specifics below.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg border border-border bg-secondary/40 p-4 text-sm">
              <div className="flex items-center gap-2 font-serif font-semibold mb-2">
                <UserIcon className="h-4 w-4 text-primary" /> Your Profile (auto-attached)
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-muted-foreground">
                <div><span className="text-foreground font-medium">Name:</span> {user?.name}</div>
                <div><span className="text-foreground font-medium">Father's:</span> {user?.fatherName || '—'}</div>
                <div><span className="text-foreground font-medium">Phone:</span> {user?.mobile}</div>
                <div><span className="text-foreground font-medium">Aadhar:</span> {user?.aadharNumber || '—'}</div>
                <div><span className="text-foreground font-medium">Voter ID:</span> {user?.voterIdNumber || '—'}</div>
                <div><span className="text-foreground font-medium">Address:</span> {user?.applicantAddress || '—'}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-3">
              {DOC_TYPES.map((d) => {
                const Icon = d.id === 'NOC_TRADE' ? ScrollText : d.id === 'LAND' ? Landmark : Users;
                const active = type === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => setType(d.id)}
                    className={`text-left p-4 rounded-xl border-2 transition-all ${active ? 'border-primary bg-primary/5 shadow-warm' : 'border-border hover:border-primary/40'}`}
                  >
                    <Icon className={`h-6 w-6 mb-2 ${active ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="font-serif font-semibold">{d.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{d.subtitle}</div>
                  </button>
                );
              })}
            </div>

            {type && (
              <div className="space-y-5 pt-2 border-t border-border animate-fade-in-up">
                <div>
                  <Label className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> A&rsquo;khing / Village Name</Label>
                  <Input value={akhing} onChange={(e) => setAkhing(e.target.value)} placeholder="e.g. Rongram A&rsquo;khing" className="mt-1.5" />
                  <div className="text-xs text-muted-foreground mt-1">Routing is automatic based on the Mauza of the A&rsquo;khing&rsquo;s Nokma Sani.</div>
                </div>

                {type === 'NOC_TRADE' && (
                  <>
                    <div>
                      <Label className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" /> Nature of Business for this entry</Label>
                      <RadioGroup value={natureOverride} onValueChange={setNatureOverride} className="mt-2 grid grid-cols-3 gap-2">
                        {['Trader', 'Labour', 'Hawker'].map((p) => (
                          <label key={p} className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer ${natureOverride === p ? 'border-primary bg-primary/5' : 'border-border'}`}>
                            <RadioGroupItem value={p} />
                            <span className="text-sm font-medium">{p}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                    <div>
                      <Label>Place of Work / Trade for this A&rsquo;khing</Label>
                      <Textarea value={placeOverride} onChange={(e) => setPlaceOverride(e.target.value)} rows={2} className="mt-1.5" placeholder="Specify the exact location/market/area" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label>Number of days <span className="text-xs text-muted-foreground">(max 89)</span></Label>
                        <span className="text-primary font-bold">{days[0]} days</span>
                      </div>
                      <Slider value={days} onValueChange={setDays} min={1} max={89} step={1} className="mt-3" />
                    </div>
                  </>
                )}

                {type === 'LAND' && (
                  <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label>1. Land Document Number</Label>
                        <Input value={land.documentNumber} onChange={(e) => setL('documentNumber', e.target.value)} placeholder="e.g. LD-2026-001" className="mt-1.5" />
                      </div>
                      <div>
                        <Label>Total Area</Label>
                        <Input value={land.totalArea} onChange={(e) => setL('totalArea', e.target.value)} placeholder="e.g. 0.5 acres / 2,500 sq ft" className="mt-1.5" />
                      </div>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 font-serif font-semibold">
                        <Compass className="h-4 w-4 text-primary" /> Boundaries
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4 mt-3">
                        <div>
                          <Label>North</Label>
                          <Input value={land.boundaryNorth} onChange={(e) => setL('boundaryNorth', e.target.value)} placeholder="Adjoining property / road / etc." className="mt-1.5" />
                        </div>
                        <div>
                          <Label>South</Label>
                          <Input value={land.boundarySouth} onChange={(e) => setL('boundarySouth', e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                          <Label>East</Label>
                          <Input value={land.boundaryEast} onChange={(e) => setL('boundaryEast', e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                          <Label>West</Label>
                          <Input value={land.boundaryWest} onChange={(e) => setL('boundaryWest', e.target.value)} className="mt-1.5" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <Label>Additional Notes (optional)</Label>
                      <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} className="mt-1.5" placeholder="Any additional context for the Nokma Sani's review" />
                    </div>
                  </div>
                )}

                {type === 'OTHER' && (
                  <div>
                    <Label>Specify Document & Purpose</Label>
                    <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)} rows={3} className="mt-1.5" placeholder="e.g. Residence certificate for school admission" />
                  </div>
                )}

                <Button onClick={submit} disabled={submitting} className="w-full bg-primary hover:bg-primary/90 gap-2">
                  <Send className="h-4 w-4" /> {submitting ? 'Submitting...' : 'Submit application'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default NewApplication;
